import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type {
  CartItem,
  CartLine,
  CartLineOperationPrice,
  Catalog,
  StockStatus,
} from "@/api/types";
import { useCartSelection } from "@/components/cart/CartSelectionContext";
import { useDisplayMoney } from "@/lib/currency";
import { minorToMajor } from "@/lib/money";
import { formatStockUom as formatUom } from "@/lib/dimensionDisplay";
import { getOperationTone } from "@/lib/operationTone";
import { cx } from "@/lib/utils";
import type {
  CartLineSummaryProps,
  CartLineUpdatePayload,
} from "@/processes/types";

export function StockStatusBadge({
  status,
  leadTimeDays,
  availableQty,
  uom,
}: {
  status?: StockStatus | null;
  /** KAN-1839/1851 — dny z varianty / materiálu obchodu; chybí-li, text nenese žádné číslo. */
  leadTimeDays?: number | null;
  /** KAN-1851 — u `partial` kolik je skladem, v jednotce `uom`. */
  availableQty?: number | null;
  uom?: string | null;
}): JSX.Element | null {
  const { t } = useTranslation("quote");
  if (!status || status === "not_managed") return null;
  const styles: Record<Exclude<StockStatus, "not_managed">, string> = {
    in_stock: "bg-ok-bg text-ok",
    low: "bg-warn-bg text-warn",
    partial: "bg-warn-bg text-warn",
    out_of_stock: "bg-warn-bg text-warn",
  };
  const labels: Record<Exclude<StockStatus, "not_managed">, string> = {
    in_stock: t("stockStatus.inStock", {
      defaultValue: "Skladem - expedice ihned",
    }),
    low: t("stockStatus.low", { defaultValue: "Nizka zasoba - overime" }),
    partial:
      leadTimeDays != null && leadTimeDays > 0
        ? t("stockStatus.partialLead", {
            count: leadTimeDays,
            qty: availableQty ?? 0,
            unit: formatUom(uom),
            defaultValue: "Skladem {{qty}} {{unit}}, zbytek +{{count}} dni",
          })
        : t("stockStatus.partial", {
            qty: availableQty ?? 0,
            unit: formatUom(uom),
            defaultValue: "Skladem {{qty}} {{unit}}, zbytek overime",
          }),
    out_of_stock:
      leadTimeDays != null && leadTimeDays > 0
        ? t("stockStatus.outOfStockLead", {
            count: leadTimeDays,
            defaultValue: "Neni skladem - +{{count}} dni",
          })
        : t("stockStatus.outOfStock", { defaultValue: "Neni skladem" }),
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-medium ${styles[status]}`}
      data-testid={`cart-line-stock-${status}`}
    >
      {labels[status]}
    </span>
  );
}

interface Props {
  line: {
    id: number;
    material_code: string | null;
    quantity: number;
    unit_price_czk: number;
    line_total_czk: number;
    config_snapshot: Record<string, unknown> | null;
    stock_status?: StockStatus | null;
    stock_lead_time_days?: number | null;
    stock_available_qty?: number | null;
    stock_uom?: string | null;
    customer_note?: string | null;
    /** Per-line ladder override (PR7). NULL = inherit cart-wide. */
    ladder_tier_id?: number | null;
    /** KAN-1047 — set when nobody has priced this variant yet. The two
     *  money fields above are then ZERO, so this row must NOT print
     *  them: "0 Kc" reads as free, which is the one thing the shop has
     *  not agreed to. */
    unpriced_reason?: { error_code: string } | null;
    /** KAN-1027 — z čeho se ``unit_price_czk`` a ``line_total_czk``
     *  skládají. Prázdné/chybějící = rozpad se nevykreslí. */
    operation_prices?: CartLineOperationPrice[];
  };
  catalog: Catalog;
  locale: string;
  configSnapshot: Record<string, unknown> | null;
  CartLineSummary: ComponentType<CartLineSummaryProps>;
  editHref: string;
  editLabel: string;
  onRemove: () => void;
  removeLabel: string;
  processSlug: string;
  /** Tone source resolved by ``operationToneColor`` — the process's
   *  operation-category hex, or its Tailwind colour token. */
  toneColor?: string | null;
  /** 1-based label rendered as a "V1"/"V2" badge. */
  variantIndex: number;
  /**
   * When false, the selection checkbox is hidden — used by surfaces
   * that don't run bulk actions (e.g. ``MyProjectDetail`` which uses
   * PartCard for project items). The variant badge stays so customers
   * still see the V1/V2 ordering. Default true.
   */
  selectable?: boolean;
  /**
   * Parent CartItem — forwarded to ``CartLineSummary`` so it can
   * address chain operations (e.g. the laser finish dropdown needs
   * ``item.id`` + ``item.operations`` to wire add/remove of the
   * surface_finishes chain op). Optional for backward compat with
   * callsites that haven't been updated yet.
   */
  item?: CartItem;
  /** Forwarded to ``CartLineSummary`` — see types.ts docstring. */
  onUpdateLine?: (lineId: number, payload: CartLineUpdatePayload) => void;
  /** Popisek operace pro rozpad ceny (KAN-1027). Bez něj se vypíše
   *  syrový ``process_type`` — čitelné, ale ne hezké; proto je to
   *  resolver od rodiče, který ceník procesů beztak drží, a ne druhý
   *  dotaz odsud. */
  processLabel?: (processType: string) => string;
  /** Účetní měna košíku (``Cart.currency_code``) — minor jednotky
   *  rozpadu se jí převádějí na major. ``format`` z ``useDisplayMoney``
   *  pak řeší zobrazovací měnu; to jsou dvě různé otázky. */
  currencyCode?: string | null;
}

/**
 * Cart variant row — one ``OrderLine`` inside a ``PartCard``.
 *
 * Adds a selection checkbox + variant badge in front of the
 * process-specific summary cells (reused from
 * ``processes/{slug}/CartLineSummary``). The summary component drives
 * what columns appear per process — each process shows its own axes,
 * print_3d shows filament+infill, etc. — so this row stays slim.
 *
 * Bulk apply actions are wired in PR3; PR2 ships the checkbox + count
 * surface only so the structure is ready.
 */
export function VariantRow({
  line,
  catalog,
  locale,
  configSnapshot,
  CartLineSummary,
  editHref,
  editLabel,
  onRemove,
  removeLabel,
  processSlug,
  toneColor,
  variantIndex,
  selectable = true,
  item,
  onUpdateLine,
  processLabel,
  currencyCode,
}: Props): JSX.Element {
  const { selected, toggle } = useCartSelection();
  const { t } = useTranslation("quote");
  // KAN-2121 — popisky karet na telefonu = záhlaví tabulky košíku.
  const { t: tCart } = useTranslation("cart");
  const isSelected = selectable && selected.has(line.id);
  const tone = getOperationTone(toneColor);
  const { format } = useDisplayMoney(locale);

  const note = (line.customer_note ?? "").trim();

  // KAN-1027 — rozpad se nabízí, jen když má co rozpadnout: u jediné
  // operace bez setup fee by jen zopakoval kus-cenu o řádek výš (táž
  // úvaha jako KAN-1036 u konfigurátoru). Naopak právě setup fee je to,
  // co při množství 1 dělá z „85 Kč/ks" a „134 Kč celkem" dvě čísla,
  // která zákazník nemá jak srovnat — proto stačí i sám o sobě.
  const shares = line.unpriced_reason ? [] : (line.operation_prices ?? []);
  // Sčítá se v MINOR a do major se převádí až výsledek — každý krok
  // v major zaokrouhluje znovu.
  const setupFeeMinor = shares.reduce(
    (sum, share) => sum + (share.setup_fee_minor ?? 0),
    0,
  );
  const showBreakdown = shares.length > 1 || setupFeeMinor > 0;

  return (
    <>
      <tr
        className="border-b border-border last:border-b-0 transition-colors"
        style={isSelected ? { background: tone.bg } : undefined}
        data-testid={`cart-line-${line.id}`}
      >
        <td className="px-3 py-3 align-top">
          <div className="inline-flex items-center gap-2">
            {selectable && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggle(line.id, processSlug)}
                className="h-3.5 w-3.5 cursor-pointer"
                style={{ accentColor: tone.dot }}
                data-testid={`cart-line-${line.id}-select`}
                aria-label={`Vybrat V${variantIndex}`}
              />
            )}
            <span
              className="mono inline-flex h-5 min-w-[2rem] items-center justify-center rounded px-1 text-[10.5px] font-semibold uppercase tracking-[0.04em]"
              style={{
                background: tone.bg,
                color: tone.ink,
                border: `1px solid ${tone.border}`,
              }}
              data-testid={`cart-line-${line.id}-variant`}
            >
              V{variantIndex}
            </span>
          </div>
        </td>
        <CartLineSummary
          line={line as CartLine}
          configSnapshot={configSnapshot}
          catalog={catalog}
          item={item}
          onUpdateLine={onUpdateLine}
        />
        <td
          className="mono px-3 py-3 text-right tabular-nums align-top text-ink-2"
          data-label={tCart("col.unitPrice", { currency: currencyCode ?? "" })}
        >
          <div className="flex flex-col items-end gap-1">
            {/* KAN-1047 — u nenaceneneho radku se cena NEVYPISUJE. Nula
                by tvrdila "zdarma"; pravda je "zatim nevime" a zakaznik
                ji musi videt na tom miste, kde cislo ceka. */}
            <span>
              {line.unpriced_reason ? t("line.unpriced") : format(line.unit_price_czk)}
            </span>
            <StockStatusBadge
              status={line.stock_status}
              leadTimeDays={line.stock_lead_time_days}
              availableQty={line.stock_available_qty}
              uom={line.stock_uom}
            />
          </div>
        </td>
        <td
          className={cx(
            "mono px-3 py-3 text-right tabular-nums align-top font-medium",
            line.unpriced_reason ? "text-warn" : "text-ink",
          )}
          data-testid={line.unpriced_reason ? `cart-line-${line.id}-unpriced` : undefined}
          data-label={tCart("col.lineTotal")}
        >
          {line.unpriced_reason ? t("line.unpricedTotal") : format(line.line_total_czk)}
        </td>
        <td className="px-3 py-3 align-top text-right">
          <div className="flex items-center justify-end gap-2">
            <Link
              to={editHref}
              className="rounded-md px-2 py-1 text-xs text-ink-3 transition-colors hover:bg-bg hover:text-ink"
              data-testid={`cart-line-${line.id}-edit`}
            >
              {editLabel}
            </Link>
            <button
              type="button"
              onClick={onRemove}
              aria-label={removeLabel}
              className="rounded-md p-1 text-ink-3 transition-colors hover:bg-danger-bg hover:text-danger"
              data-testid={`cart-line-${line.id}-remove`}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </td>
      </tr>
      {showBreakdown && (
        <tr
          className="border-b border-border last:border-b-0"
          style={isSelected ? { background: tone.bg } : undefined}
          data-testid={`cart-line-${line.id}-breakdown`}
        >
          <td className="px-3 pb-3 align-top" aria-hidden="true" />
          <td colSpan={99} className="px-3 pb-3">
            <ul className="space-y-0.5 text-xs text-ink-3">
              {shares.map((share, index) => (
                <li
                  key={share.operation_id ?? `${share.process_type}:${index}`}
                  className="flex items-center justify-between gap-4"
                  data-testid={`cart-line-${line.id}-breakdown-${share.process_type}`}
                >
                  <span>{processLabel?.(share.process_type) ?? share.process_type}</span>
                  <span className="mono tabular-nums">
                    {t("line.breakdownPerPiece", {
                      defaultValue: "{{price}}/ks",
                      price: format(minorToMajor(share.unit_price_minor, currencyCode)),
                    })}
                  </span>
                </li>
              ))}
              {setupFeeMinor > 0 && (
                <li
                  className="flex items-center justify-between gap-4"
                  data-testid={`cart-line-${line.id}-breakdown-setup`}
                >
                  {/* Vlastní řádek, ne přičtení ke kus-ceně: účtuje se
                      jednou na řádek, takže sloučit ho by lhalo při
                      každém množství > 1. */}
                  <span>
                    {t("line.breakdownSetup", {
                      defaultValue: "Příprava výroby (jednorázově)",
                    })}
                  </span>
                  <span className="mono tabular-nums">
                    {format(minorToMajor(setupFeeMinor, currencyCode))}
                  </span>
                </li>
              )}
            </ul>
          </td>
        </tr>
      )}
      {note && (
        <tr
          className="border-b border-border last:border-b-0"
          style={isSelected ? { background: tone.bg } : undefined}
          data-testid={`cart-line-${line.id}-note`}
        >
          <td className="px-3 pb-3 align-top" aria-hidden="true" />
          <td
            colSpan={99}
            className="px-3 pb-3 text-xs italic text-ink-3 whitespace-pre-wrap"
          >
            <span aria-hidden="true">💬 </span>
            {note}
          </td>
        </tr>
      )}
    </>
  );
}
